/**
 * Input Validation Utilities for AI Guardrails
 * Implements character filtering, length limits, and prompt injection detection
 */

// Configuration constants
const MAX_INPUT_LENGTH = 200;

// Allowed characters: alphanumeric + educational symbols
// Includes: A-Z, a-z, 0-9, space, period, comma, hyphen, apostrophe,
// ampersand, plus, forward slash, parentheses, quotes, question mark, exclamation
const ALLOWED_CHARS_REGEX = /^[A-Za-z0-9\s.,\-'&+/()\"?!]+$/;

// Prompt injection detection patterns
const INJECTION_PATTERNS = [
  // Direct instruction attempts
  /ignore\s+(previous|above|prior|all)\s+(instructions|prompts|rules|directives)/i,
  /disregard\s+(previous|above|prior|all)\s+(instructions|prompts|rules|directives)/i,
  /forget\s+(previous|above|prior|all)\s+(instructions|prompts|rules|directives)/i,

  // System role manipulation
  /system\s*:/i,
  /assistant\s*:/i,
  /\[system\]/i,
  /\<system\>/i,
  /you\s+are\s+now/i,
  /new\s+(instructions|prompt|role)/i,

  // Prompt boundary escape attempts
  /```\s*system/i,
  /```\s*assistant/i,
  /<\|.*?\|>/,  // Special tokens
  /\{\{.*?system.*?\}\}/i,

  // Command injection
  /\\n\\nsystem/i,
  /\n\nsystem/i,
  /---\s*system/i,

  // Role-play escape attempts
  /act\s+as\s+if/i,
  /pretend\s+(to\s+be|you\s+are)/i,
  /roleplay\s+as/i,

  // Output manipulation
  /print\s+your\s+(instructions|prompt|system)/i,
  /reveal\s+your\s+(instructions|prompt|system)/i,
  /show\s+(me\s+)?your\s+(instructions|prompt|system)/i,

  // Jailbreak attempts
  /DAN\s+mode/i,  // "Do Anything Now"
  /developer\s+mode/i,
  /jailbreak/i,
  /bypass\s+(safety|filter|guardrail)/i
];

// Keywords that indicate potentially harmful or prohibited content
const CONTENT_POLICY_KEYWORDS = {
  // Severity levels: 'block' (immediate rejection) or 'flag' (human review)
  block: [
    // Violence and harm
    'bomb', 'weapon', 'murder', 'kill', 'terrorist', 'violence', 'suicide',
    'self-harm', 'cutting', 'overdose',

    // Illegal activities
    'drug dealing', 'trafficking', 'fraud', 'scam', 'hack', 'exploit',
    'piracy', 'steal', 'theft', 'illegal',

    // Child safety
    'child abuse', 'child exploitation', 'csam', 'underage',

    // Hate speech
    'hate speech', 'genocide', 'ethnic cleansing',

    // Adult content (educational context)
    'pornography', 'explicit content', 'sexual content'
  ],

  flag: [
    // Borderline topics that might be legitimate in educational context
    'weapons', 'hacking', 'security', 'penetration testing', 'cryptography',
    'drugs', 'alcohol', 'cannabis', 'marijuana', 'cryptocurrency',
    'politics', 'controversial', 'debate'
  ]
};

/**
 * Validation result object structure
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether the input passes validation
 * @property {string[]} errors - Array of error messages
 * @property {string[]} warnings - Array of warning messages
 * @property {string} severity - 'block' or 'flag' for content policy violations
 */

/**
 * Validates input length
 * @param {string} input - User input to validate
 * @returns {ValidationResult}
 */
export function validateLength(input) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  if (!input || input.trim().length === 0) {
    result.isValid = false;
    result.errors.push('Please enter a subject to learn about');
    return result;
  }

  if (input.length > MAX_INPUT_LENGTH) {
    result.isValid = false;
    result.errors.push(
      `Course topic is too long (${input.length}/${MAX_INPUT_LENGTH} characters). Please shorten your description.`
    );
  }

  return result;
}

/**
 * Validates allowed characters
 * @param {string} input - User input to validate
 * @returns {ValidationResult}
 */
export function validateCharacters(input) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  if (!ALLOWED_CHARS_REGEX.test(input)) {
    result.isValid = false;

    // Find the first invalid character for better error message
    const invalidChars = new Set();
    for (const char of input) {
      if (!ALLOWED_CHARS_REGEX.test(char) && char !== ' ') {
        invalidChars.add(char);
      }
    }

    const charList = Array.from(invalidChars).slice(0, 5).join(', ');
    const moreCount = invalidChars.size > 5 ? ` and ${invalidChars.size - 5} more` : '';

    result.errors.push(
      `Invalid characters detected: ${charList}${moreCount}. ` +
      `Only letters, numbers, and basic punctuation (.,?!-'&+/\"()) are allowed.`
    );
  }

  return result;
}

/**
 * Detects potential prompt injection attempts
 * @param {string} input - User input to validate
 * @returns {ValidationResult}
 */
export function detectPromptInjection(input) {
  const result = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const lowerInput = input.toLowerCase();

  for (const pattern of INJECTION_PATTERNS) {
    if (pattern.test(input)) {
      result.isValid = false;
      result.errors.push(
        'Your input contains patterns that are not allowed. Please describe your course topic naturally.'
      );
      return result; // Stop on first match to avoid revealing specific patterns
    }
  }

  // Additional heuristics
  // Check for excessive special characters that might indicate injection
  const specialCharCount = (input.match(/[{}[\]<>|\\]/g) || []).length;
  if (specialCharCount > 3) {
    result.warnings.push(
      'Your input contains unusual formatting. Please use simple, natural language.'
    );
  }

  // Check for multiple consecutive newlines (potential boundary escape)
  if (/\n\n+/.test(input)) {
    result.isValid = false;
    result.errors.push('Multiple line breaks are not allowed. Please write your topic on a single line.');
  }

  return result;
}

/**
 * Checks content against policy keywords
 * @param {string} input - User input to validate
 * @returns {ValidationResult}
 */
export function checkContentPolicy(input) {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    severity: null
  };

  const lowerInput = input.toLowerCase();

  // Check for immediate blocks
  for (const keyword of CONTENT_POLICY_KEYWORDS.block) {
    if (lowerInput.includes(keyword.toLowerCase())) {
      result.isValid = false;
      result.severity = 'block';
      result.errors.push(
        'This topic appears to violate our content policy. We cannot generate courses on harmful, illegal, or inappropriate subjects. ' +
        'Please choose a different topic.'
      );
      return result; // Stop on first block-level match
    }
  }

  // Check for flagged content (requires review)
  for (const keyword of CONTENT_POLICY_KEYWORDS.flag) {
    if (lowerInput.includes(keyword.toLowerCase())) {
      result.severity = 'flag';
      result.warnings.push(
        'This topic will be reviewed to ensure it complies with our usage policies.'
      );
      // Don't return here - allow the request but flag it
      break;
    }
  }

  return result;
}

/**
 * Comprehensive input validation combining all checks
 * @param {string} input - User input to validate
 * @returns {ValidationResult}
 */
export function validateInput(input) {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    severity: null
  };

  // Trim input for processing
  const trimmedInput = input.trim();

  // Run all validation checks
  const lengthCheck = validateLength(trimmedInput);
  const charCheck = validateCharacters(trimmedInput);
  const injectionCheck = detectPromptInjection(trimmedInput);
  const policyCheck = checkContentPolicy(trimmedInput);

  // Combine results
  result.errors = [
    ...lengthCheck.errors,
    ...charCheck.errors,
    ...injectionCheck.errors,
    ...policyCheck.errors
  ];

  result.warnings = [
    ...lengthCheck.warnings,
    ...charCheck.warnings,
    ...injectionCheck.warnings,
    ...policyCheck.warnings
  ];

  result.isValid = lengthCheck.isValid &&
                   charCheck.isValid &&
                   injectionCheck.isValid &&
                   policyCheck.isValid;

  result.severity = policyCheck.severity;

  return result;
}

/**
 * Sanitizes input for safe use in AI prompts
 * This is a defense-in-depth measure even after validation
 * @param {string} input - User input to sanitize
 * @returns {string} - Sanitized input
 */
export function sanitizeForPrompt(input) {
  // Trim whitespace
  let sanitized = input.trim();

  // Remove any potential control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  // Normalize multiple spaces to single space
  sanitized = sanitized.replace(/\s+/g, ' ');

  // Remove any newlines (should already be caught by validation)
  sanitized = sanitized.replace(/[\r\n]+/g, ' ');

  return sanitized;
}

/**
 * Gets user-friendly error message for display
 * @param {ValidationResult} validationResult - Result from validateInput
 * @returns {string} - User-friendly error message
 */
export function getValidationErrorMessage(validationResult) {
  if (validationResult.isValid) {
    return '';
  }

  // Return the first error (most relevant)
  return validationResult.errors[0] || 'Invalid input. Please try again.';
}

/**
 * Configuration export for use in other modules
 */
export const ValidationConfig = {
  MAX_INPUT_LENGTH,
  ALLOWED_CHARS_REGEX
};
